import gsap from 'gsap';
import { Container, FillGradient, Graphics } from 'pixi.js';
import { gameConfig } from '../config/gameConfig.js';

/** Coin (Cleopatra) — pays anywhere; tease when 2+ land on distinct reels. */
const COIN_SCATTER_ID = 10;
/** Star (pyramides) — 1st / 3rd / 5th reels only (0-based: 0, 2, 4). */
const STAR_SCATTER_ID = 11;

/**
 * @param {number[][]} grid
 * @param {number} symbolId
 * @param {number[] | null} eligibleReels `null` = any reel counts
 * @param {number} [throughCol] only columns 0..throughCol are visible/landed
 * @returns {number} reels (columns) that contain the symbol at least once
 */
export function countReelsWithSymbol(grid, symbolId, eligibleReels, throughCol) {
  if (!Array.isArray(grid)) return 0;
  const eligible = eligibleReels ? new Set(eligibleReels) : null;
  const lastCol = throughCol != null ? throughCol : grid.length - 1;
  let n = 0;
  for (let col = 0; col <= lastCol && col < grid.length; col++) {
    if (eligible && !eligible.has(col)) continue;
    const colSyms = grid[col];
    if (!Array.isArray(colSyms)) continue;
    if (colSyms.some((id) => id === symbolId)) n += 1;
  }
  return n;
}

/**
 * @param {number[][]} grid
 * @param {number} symbolId
 * @param {number} minMatch
 * @param {number[] | null} eligibleReels
 * @param {number} throughCol
 * @returns {number} first anticipated reel index, or -1
 */
function findSameSymbolAnticipationStart(grid, symbolId, minMatch, eligibleReels, throughCol) {
  if (!Array.isArray(grid) || !grid.length) return -1;
  const eligible = eligibleReels ? [...eligibleReels].sort((a, b) => a - b) : null;
  const lastCol = throughCol != null ? throughCol : grid.length - 1;

  let matchCount = 0;
  for (let col = 0; col <= lastCol && col < grid.length; col++) {
    const colSyms = grid[col];
    if (!Array.isArray(colSyms)) continue;

    const reelEligible = !eligible || eligible.includes(col);
    if (reelEligible && colSyms.some((id) => id === symbolId)) {
      matchCount += 1;
    }

    if (matchCount >= minMatch) {
      if (!eligible) {
        const next = col + 1;
        return next < grid.length ? next : -1;
      }
      const nextEligible = eligible.find((r) => r > col);
      return nextEligible ?? -1;
    }
  }
  return -1;
}

/**
 * First reel that should run slower / show tease FX.
 * Only triggers when **one scatter type** already has `minMatch` on landed reels
 * (e.g. two coins, or two stars on eligible reels — not coin + star).
 * @param {number[][]} grid column-major [reel][row]
 * @param {number} [throughCol] landed reels only — required for fair tease (do not pass full grid for motion)
 * @returns {number}
 */
export function findAnticipationStartCol(grid, throughCol) {
  const fx = gameConfig.visualFx ?? {};
  const minMatch = fx.anticipationMinScatters ?? 2;
  const starReels = fx.anticipationStarScatterReels ?? [0, 2, 4];
  const lastCol = throughCol != null ? throughCol : grid.length - 1;

  const starts = [
    findSameSymbolAnticipationStart(grid, COIN_SCATTER_ID, minMatch, null, lastCol),
    findSameSymbolAnticipationStart(grid, STAR_SCATTER_ID, minMatch, starReels, lastCol),
  ].filter((c) => c >= 0);

  if (!starts.length) return -1;
  return Math.min(...starts);
}

/**
 * Scatter symbol ids that qualify for tease after `throughCol` has landed.
 * @param {number[][]} grid
 * @param {number} throughCol
 * @returns {number[]}
 */
export function getQualifyingTeaseSymbolIds(grid, throughCol) {
  const fx = gameConfig.visualFx ?? {};
  const minMatch = fx.anticipationMinScatters ?? 2;
  const starReels = fx.anticipationStarScatterReels ?? [0, 2, 4];
  /** @type {number[]} */
  const ids = [];
  if (countReelsWithSymbol(grid, COIN_SCATTER_ID, null, throughCol) >= minMatch) {
    ids.push(COIN_SCATTER_ID);
  }
  if (countReelsWithSymbol(grid, STAR_SCATTER_ID, starReels, throughCol) >= minMatch) {
    ids.push(STAR_SCATTER_ID);
  }
  return ids;
}

/**
 * Column to highlight after reel `landedCol` stops (next anticipated reel).
 * @param {number[][]} grid
 * @param {number} landedCol
 * @returns {number}
 */
export function getAnticipationTeaseColumn(grid, landedCol) {
  if (!Array.isArray(grid) || landedCol < 0) return -1;
  if (findAnticipationStartCol(grid, landedCol) < 0) return -1;

  const profile = computeAnticipationProfile(grid, false, landedCol);
  for (let c = landedCol + 1; c < profile.anticipated.length; c++) {
    if (profile.anticipated[c]) return c;
  }
  return -1;
}

/**
 * @param {number[][]} grid
 * @param {boolean} [disabled]
 * @param {number | null} [throughCol] only count scatters on columns 0..throughCol
 *   (pass the last landed reel so motion/tease never spoil from the full result)
 * @returns {{ anticipated: boolean[], extraCells: number[], startCol: number }}
 */
export function computeAnticipationProfile(grid, disabled = false, throughCol = null) {
  const reelCount = gameConfig.reelCount ?? 5;
  const anticipated = new Array(reelCount).fill(false);
  const extraCells = new Array(reelCount).fill(0);
  if (disabled || !Array.isArray(grid)) {
    return { anticipated, extraCells, startCol: -1 };
  }

  const fx = gameConfig.visualFx ?? {};
  const minMatch = fx.anticipationMinScatters ?? 2;
  const starReels = fx.anticipationStarScatterReels ?? [0, 2, 4];
  const lastCol = throughCol != null ? throughCol : grid.length - 1;

  const coinStart = findSameSymbolAnticipationStart(
    grid,
    COIN_SCATTER_ID,
    minMatch,
    null,
    lastCol,
  );
  const starStart = findSameSymbolAnticipationStart(
    grid,
    STAR_SCATTER_ID,
    minMatch,
    starReels,
    lastCol,
  );

  /** @type {boolean[]} */
  const coinAnticipated = new Array(reelCount).fill(false);
  /** @type {boolean[]} */
  const starAnticipated = new Array(reelCount).fill(false);

  if (coinStart >= 0) {
    for (let col = coinStart; col < reelCount; col++) {
      coinAnticipated[col] = true;
    }
  }
  if (starStart >= 0) {
    for (let col = starStart; col < reelCount; col++) {
      if (starReels.includes(col)) {
        starAnticipated[col] = true;
      }
    }
  }

  for (let col = 0; col < reelCount; col++) {
    anticipated[col] = coinAnticipated[col] || starAnticipated[col];
  }

  const starts = [coinStart, starStart].filter((c) => c >= 0);
  const startCol = starts.length ? Math.min(...starts) : -1;
  if (startCol < 0) {
    return { anticipated, extraCells, startCol: -1 };
  }

  const extraMin = fx.anticipationExtraCellsMin ?? 6;
  const extraSpread = fx.anticipationExtraCellsSpread ?? 4;
  for (let col = 0; col < reelCount; col++) {
    if (!anticipated[col]) continue;
    extraCells[col] = extraMin + Math.floor(Math.random() * extraSpread);
  }
  return { anticipated, extraCells, startCol };
}

/**
 * Pulsing gold column frame shown while a reel is in scatter anticipation.
 */
export class AnticipationColumnFx {
  /**
   * @param {{
   *   parent: import('pixi.js').Container,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   * }} opts
   */
  constructor(opts) {
    this._parent = opts.parent;
    this._animationController = opts.animationController;
    this._root = new Container();
    this._root.eventMode = 'none';
    this._root.zIndex = 0.42;
    this._parent.addChild(this._root);

    /** @type {Map<number, { host: Container, pulseTween: import('gsap').Tween | null }>} */
    this._columns = new Map();
    this._activeCol = -1;
  }

  /**
   * @param {number} col
   */
  showColumn(col) {
    if (this._activeCol === col) return;
    this.hideAll();

    const cw = gameConfig.cellWidth;
    const ch = gameConfig.cellHeight;
    const blockH = gameConfig.rowCount * ch;
    const x = col * (cw + gameConfig.reelGap) + cw / 2;

    const host = new Container();
    host.position.set(x, blockH / 2);
    host.eventMode = 'none';

    const fx = gameConfig.visualFx ?? {};
    const padX = fx.anticipationColumnPadX ?? 10;
    const padY = fx.anticipationColumnPadY ?? 8;
    const w = cw + padX * 2;
    const h = blockH + padY * 2;
    const r = fx.anticipationColumnRadius ?? 14;
    const gold = gameConfig.theme?.gold ?? 0xe7c463;

    const glow = new Graphics();
    const grad = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: gold },
        { offset: 0.5, color: gameConfig.theme?.goldBright ?? 0xfff8dc },
        { offset: 1, color: gold },
      ],
    });
    glow.roundRect(-w / 2, -h / 2, w, h, r);
    glow.stroke({ width: 5, color: gold, alpha: 0.55 });
    glow.roundRect(-w / 2 + 2, -h / 2 + 2, w - 4, h - 4, r - 2);
    glow.fill({ fill: grad, alpha: 0.14 });
    host.addChild(glow);

    const beam = new Graphics();
    beam.rect(-cw * 0.22, -h / 2, cw * 0.44, h);
    beam.fill({ color: gameConfig.theme?.skyGlow ?? 0xe7c463, alpha: 0.1 });
    host.addChild(beam);

    host.alpha = 0.75;
    host.scale.set(1, 1);
    this._root.addChild(host);

    const pulseTween = gsap.to(host, {
      alpha: 1,
      scaleX: 1.03,
      scaleY: 1.02,
      duration: fx.anticipationPulseSec ?? 0.42,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
    this._animationController.trackTween(pulseTween);

    this._columns.set(col, { host, pulseTween, grad });
    this._activeCol = col;
  }

  hideAll() {
    for (const [, entry] of this._columns) {
      if (entry.pulseTween) {
        entry.pulseTween.kill();
        this._animationController.releaseTween(entry.pulseTween);
      }
      entry.host.parent?.removeChild(entry.host);
      entry.host.destroy({ children: true });
      // destroy({ children: true }) does not free gradient textures; release explicitly.
      entry.grad?.destroy?.();
    }
    this._columns.clear();
    this._activeCol = -1;
  }

  destroy() {
    this.hideAll();
    this._root.parent?.removeChild(this._root);
    this._root.destroy({ children: true });
  }
}
